import { useState, useEffect } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

export const useResponsive = () => {
  const theme = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  const [screenSize, setScreenSize] = useState('desktop');

  // Material-UI breakpoints
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.only('lg'));
  const isXl = useMediaQuery(theme.breakpoints.only('xl'));

  // Responsive queries
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    setIsMobile(isMobileDevice);
    
    if (isXs) {
      setScreenSize('mobile');
    } else if (isSm) {
      setScreenSize('mobile-large');
    } else if (isMd) {
      setScreenSize('tablet');
    } else if (isLg) {
      setScreenSize('desktop');
    } else if (isXl) {
      setScreenSize('desktop-large');
    }
  }, [isXs, isSm, isMd, isLg, isXl, isMobileDevice]);

  // Touch device detection
  const isTouchDevice = () => {
    return (('ontouchstart' in window) ||
           (navigator.maxTouchPoints > 0) ||
           (navigator.msMaxTouchPoints > 0));
  };

  // Orientation detection
  const [orientation, setOrientation] = useState('portrait');

  useEffect(() => {
    const handleOrientationChange = () => {
      if (window.innerHeight > window.innerWidth) {
        setOrientation('portrait');
      } else {
        setOrientation('landscape');
      }
    };

    handleOrientationChange();
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return {
    // Screen size booleans
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    
    // Device type booleans
    isMobile: isMobileDevice,
    isTablet,
    isDesktop,
    
    // Screen size string
    screenSize,
    
    // Touch and orientation
    isTouchDevice: isTouchDevice(),
    orientation,
    
    // Utility functions
    getGridColumns: () => {
      if (isXs) return 1;
      if (isSm) return 2;
      if (isMd) return 3;
      return 4;
    },
    
    getDrawerWidth: () => {
      if (isMobileDevice) return '100%';
      return 240;
    },
    
    getSidebarVariant: () => {
      if (isMobileDevice) return 'temporary';
      return 'permanent';
    },
    
    getAppBarHeight: () => {
      if (isMobileDevice) return 56;
      return 64;
    },
    
    getContentPadding: () => {
      if (isXs) return theme.spacing(1);
      if (isSm) return theme.spacing(2);
      return theme.spacing(3);
    }
  };
};

export default useResponsive;